import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Calculator,
  Sparkles,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Info,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * ProbabilityCalculator
 * --------------------------------------------------------------
 * Helps admins calibrate per-prize probability weights so the
 * prize pool depletes at the right pace over the campaign.
 *
 * Auto-detects:
 *   • premium lots  = winning + limited stock + still available
 *   • consolation   = everything else (unlimited winning, losing,
 *                     or depleted segments)
 *
 * Inputs:
 *   • expected total plays for the campaign
 *   • how many premium lots to distribute (default = count of
 *     available premium segments)
 *
 * Outputs the optimal per-lot % and a one-click apply button
 * that rewrites segment weights so the pool totals 100 % exactly.
 */
export default function ProbabilityCalculator({ segments, onApply }) {
  const { t } = useTranslation();
  const segs = segments || [];

  // --- Auto-detection ---------------------------------------------------
  const premiumIndexes = useMemo(
    () =>
      segs
        .map((s, i) => ({ s, i }))
        .filter(
          ({ s }) =>
            s.is_winning &&
            s.initial_quantity != null &&
            (parseInt(s.remaining_quantity) || 0) > 0,
        )
        .map(({ i }) => i),
    [segs],
  );

  const consolationIndexes = useMemo(
    () => segs.map((_, i) => i).filter((i) => !premiumIndexes.includes(i)),
    [segs, premiumIndexes],
  );

  // Depleted (winning + limited stock with remaining = 0) are forced to 0%
  // at runtime by the backend, so we don't allocate consolation weight to
  // them — they're a sub-category of consolationIndexes.
  const depletedIndexes = useMemo(
    () =>
      segs
        .map((s, i) => ({ s, i }))
        .filter(
          ({ s }) =>
            s.is_winning &&
            s.initial_quantity != null &&
            (parseInt(s.remaining_quantity) || 0) <= 0,
        )
        .map(({ i }) => i),
    [segs],
  );

  const activeConsolationIndexes = useMemo(
    () => consolationIndexes.filter((i) => !depletedIndexes.includes(i)),
    [consolationIndexes, depletedIndexes],
  );

  const currentCumulative = useMemo(() => {
    const total = segs.reduce((a, s) => a + (parseFloat(s.weight) || 0), 0);
    if (total <= 0) return 0;
    const premiumSum = premiumIndexes.reduce(
      (a, i) => a + (parseFloat(segs[i].weight) || 0),
      0,
    );
    return (premiumSum / total) * 100;
  }, [segs, premiumIndexes]);

  // --- State ------------------------------------------------------------
  const [open, setOpen] = useState(false);
  const [plays, setPlays] = useState(500);
  const [lotsToDistribute, setLotsToDistribute] = useState(
    () => premiumIndexes.length || 1,
  );

  // --- Computations -----------------------------------------------------
  const numPremium = premiumIndexes.length;
  const safePlays = Math.max(1, parseInt(plays) || 0);
  // Cap lots to distribute at the actual available premium count so the
  // calculator self-corrects when the pool changes (no useEffect sync).
  const safeLots = Math.max(
    0,
    Math.min(parseInt(lotsToDistribute) || 0, numPremium),
  );
  const targetCumulative = (safeLots / safePlays) * 100;
  const perLotWeight = numPremium > 0 ? targetCumulative / numPremium : 0;
  const consolationTotalWeight = Math.max(0, 100 - perLotWeight * numPremium);
  const perConsolationWeight =
    activeConsolationIndexes.length > 0
      ? consolationTotalWeight / activeConsolationIndexes.length
      : 0;

  // Binomial helpers — compute distribution preview
  const binomialPreview = useMemo(() => {
    const p = targetCumulative / 100;
    if (p <= 0 || p >= 1) return null;
    const n = safePlays;
    // Use log-space to avoid overflow
    const logFact = (k) => {
      let s = 0;
      for (let i = 2; i <= k; i++) s += Math.log(i);
      return s;
    };
    const binomPmf = (k) =>
      Math.exp(
        logFact(n) -
          logFact(k) -
          logFact(n - k) +
          k * Math.log(p) +
          (n - k) * Math.log(1 - p),
      );
    const target = safeLots;
    const probs = {};
    let cumul = 0;
    for (let k = 0; k <= Math.min(target + 2, n); k++) {
      probs[k] = binomPmf(k);
      cumul += probs[k];
    }
    const pAtLeastTarget = 1 - cumul + (probs[target] || 0);
    const pNone = probs[0] || 0;
    const expected = n * p;
    return { expected, pNone, pAtLeastTarget };
  }, [targetCumulative, safePlays, safeLots]);

  // --- Validity & warnings ---------------------------------------------
  const noPremium = numPremium === 0;
  const noConsolation = consolationIndexes.length === 0;
  const overAllocated = perLotWeight * numPremium > 100;
  const canApply = !noPremium && !overAllocated && perLotWeight >= 0;

  // --- Apply ------------------------------------------------------------
  const handleApply = () => {
    const round3 = (x) => Math.round(x * 1000) / 1000;
    const newSegments = segs.map((s, i) => {
      if (premiumIndexes.includes(i)) {
        return { ...s, weight: round3(perLotWeight) };
      }
      if (depletedIndexes.includes(i)) {
        // Keep depleted lots at 0 — they're forced to 0 at runtime anyway
        // and showing weight 0 is the truthful state for the merchant.
        return { ...s, weight: 0 };
      }
      return { ...s, weight: round3(perConsolationWeight) };
    });
    // Reconcile rounding so total is exactly 100
    const total = newSegments.reduce((a, s) => a + (s.weight || 0), 0);
    const diff = round3(100 - total);
    if (Math.abs(diff) > 0) {
      const adjustIdx =
        activeConsolationIndexes[0] !== undefined
          ? activeConsolationIndexes[0]
          : (premiumIndexes[0] ?? 0);
      newSegments[adjustIdx].weight = round3(
        (newSegments[adjustIdx].weight || 0) + diff,
      );
    }
    onApply(newSegments);
  };

  // --- Render -----------------------------------------------------------
  return (
    <div
      className="mb-3 rounded-xl border border-amber-300/60 bg-gradient-to-br from-amber-50/70 to-orange-50/40 dark:from-amber-500/10 dark:to-orange-500/5"
      data-testid="probability-calculator"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
        data-testid="probability-calculator-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
            <Calculator className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" />
          </div>
          <div>
            <div className="text-[13px] font-semibold text-amber-900 dark:text-amber-200">
              {t("probability_calc.title", "Calculette de probabilités")}
            </div>
            <div className="text-[11px] text-amber-700/80 dark:text-amber-200/70">
              {t(
                "probability_calc.subtitle",
                "Évite le piège des probabilités cumulées",
              )}
              {" · "}
              <span className="font-mono font-semibold">
                {currentCumulative.toFixed(2)}%
              </span>{" "}
              {t("probability_calc.current_cumul", "cumulé actuellement")}
            </div>
          </div>
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 text-amber-700 dark:text-amber-300 shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-amber-700 dark:text-amber-300 shrink-0" />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {/* Detected segments summary */}
          <div className="rounded-lg bg-white/60 dark:bg-black/20 border border-amber-200/60 dark:border-amber-500/20 px-3 py-2.5 text-[11.5px] text-foreground/80 leading-relaxed">
            <div className="flex items-start gap-2">
              <Info className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                {t("probability_calc.detection", "Détection automatique")} :{" "}
                <span className="font-semibold text-amber-700 dark:text-amber-300">
                  {numPremium}{" "}
                  {t("probability_calc.premium_lots", "lot(s) premium")}
                </span>{" "}
                ({t("probability_calc.premium_def", "gagnants + stock limité + disponibles")})
                {activeConsolationIndexes.length > 0 && (
                  <>
                    {" "}
                    ·{" "}
                    <span className="font-semibold">
                      {activeConsolationIndexes.length}{" "}
                      {t(
                        "probability_calc.consolation_lots",
                        "lot(s) de consolation",
                      )}
                    </span>{" "}
                    ({t(
                      "probability_calc.consolation_def",
                      "le reste absorbe les pourcentages",
                    )})
                  </>
                )}
                {depletedIndexes.length > 0 && (
                  <>
                    {" "}
                    ·{" "}
                    <span className="font-semibold text-destructive">
                      {depletedIndexes.length}{" "}
                      {t(
                        "probability_calc.depleted_lots",
                        "lot(s) épuisé(s)",
                      )}
                    </span>{" "}
                    ({t(
                      "probability_calc.depleted_def",
                      "gardés à 0 % (déjà gagnés)",
                    )})
                  </>
                )}
              </div>
            </div>
          </div>

          {noPremium && (
            <div className="flex items-start gap-2 rounded-lg bg-destructive/10 text-destructive px-3 py-2 text-[11.5px]">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div>
                {t(
                  "probability_calc.no_premium",
                  "Aucun lot premium détecté. Active 'Gagnant' et définis une 'Quantité initiale' sur les lots à distribuer.",
                )}
              </div>
            </div>
          )}

          {/* Inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                {t("probability_calc.plays_expected", "Plays attendus")}
              </label>
              <Input
                type="number"
                min={1}
                step={50}
                value={plays}
                onChange={(e) => setPlays(e.target.value)}
                className="h-9 rounded-lg mt-1 tabular-nums"
                data-testid="probability-calc-plays"
              />
              <div className="mt-1 text-[10px] text-muted-foreground leading-snug">
                {t(
                  "probability_calc.plays_hint",
                  "Estimation totale de participations sur toute la campagne.",
                )}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                {t(
                  "probability_calc.lots_to_distribute",
                  "Lots à distribuer",
                )}
              </label>
              <Input
                type="number"
                min={0}
                max={numPremium}
                step={1}
                value={lotsToDistribute}
                onChange={(e) => setLotsToDistribute(e.target.value)}
                className="h-9 rounded-lg mt-1 tabular-nums"
                data-testid="probability-calc-lots"
              />
              <div className="mt-1 text-[10px] text-muted-foreground leading-snug">
                {t(
                  "probability_calc.lots_hint",
                  "Combien de lots premium tu veux idéalement distribuer.",
                )}
              </div>
            </div>
          </div>

          {/* Results */}
          {!noPremium && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-500/30 bg-white/80 dark:bg-black/30 px-3 py-3 space-y-2.5">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 dark:text-amber-300">
                <Sparkles className="h-3 w-3" />
                {t("probability_calc.recommendation", "Recommandation")}
              </div>
              <div className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div>
                  <div className="text-[10.5px] text-muted-foreground uppercase tracking-wider">
                    {t(
                      "probability_calc.per_premium",
                      "Poids par lot premium",
                    )}
                  </div>
                  <div className="text-2xl font-bold text-amber-700 dark:text-amber-300 tabular-nums leading-tight">
                    {perLotWeight.toFixed(3)}
                    <span className="text-sm">%</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    × {numPremium}{" "}
                    {t("probability_calc.lots", "lots")} ={" "}
                    <span className="font-semibold">
                      {(perLotWeight * numPremium).toFixed(2)}%
                    </span>{" "}
                    {t("probability_calc.cumulative", "cumulé")}
                  </div>
                </div>
                <div>
                  <div className="text-[10.5px] text-muted-foreground uppercase tracking-wider">
                    {t(
                      "probability_calc.per_consolation",
                      "Poids par consolation",
                    )}
                  </div>
                  <div className="text-2xl font-bold text-foreground tabular-nums leading-tight">
                    {perConsolationWeight.toFixed(3)}
                    <span className="text-sm">%</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    × {activeConsolationIndexes.length}{" "}
                    {t("probability_calc.lots", "lots")} ={" "}
                    <span className="font-semibold">
                      {consolationTotalWeight.toFixed(2)}%
                    </span>{" "}
                    {t("probability_calc.cumulative", "cumulé")}
                  </div>
                </div>
              </div>

              {/* Statistical preview */}
              {binomialPreview && (
                <div className="border-t border-amber-200/60 dark:border-amber-500/20 pt-2 mt-2 grid grid-cols-3 gap-2 text-[10.5px]">
                  <div>
                    <div className="text-muted-foreground">
                      {t("probability_calc.expected", "Attendu")}
                    </div>
                    <div className="font-semibold tabular-nums">
                      {binomialPreview.expected.toFixed(1)}{" "}
                      {t("probability_calc.lots", "lots")}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">
                      {t(
                        "probability_calc.p_none",
                        "P(0 lot ne tombe)",
                      )}
                    </div>
                    <div className="font-semibold tabular-nums">
                      {(binomialPreview.pNone * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">
                      {t(
                        "probability_calc.p_all",
                        "P(tout distribuer)",
                      )}
                    </div>
                    <div className="font-semibold tabular-nums">
                      {Math.max(0, binomialPreview.pAtLeastTarget * 100).toFixed(1)}%
                    </div>
                  </div>
                </div>
              )}

              {overAllocated && (
                <div className="flex items-start gap-2 rounded-lg bg-destructive/10 text-destructive px-3 py-2 text-[11.5px]">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <div>
                    {t(
                      "probability_calc.over_allocated",
                      "Trop de lots pour ce nombre de plays. Augmente les plays attendus ou réduis les lots.",
                    )}
                  </div>
                </div>
              )}

              <Button
                type="button"
                onClick={handleApply}
                disabled={!canApply}
                className="w-full h-9 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50"
                data-testid="probability-calc-apply"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                {t(
                  "probability_calc.apply_btn",
                  "Appliquer les poids",
                )}
              </Button>
              <div className="text-[10px] text-muted-foreground text-center leading-snug">
                {t(
                  "probability_calc.apply_hint",
                  "Les poids des lots premium seront uniformisés et le reste sera réparti sur les segments de consolation. Total garanti à 100 %.",
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
